import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import { 
  Link2,
  Plus,
  Copy,
  Trash2,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Mail,
  User,
  RefreshCw,
  ExternalLink,
  Power,
  PowerOff
} from "lucide-react";

export default function AdminApplicationLinks() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [links, setLinks] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newLinkData, setNewLinkData] = useState({
    applicant_email: "",
    applicant_name: ""
  });
  const [createdLink, setCreatedLink] = useState(null);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/application-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinks(response.data);
    } catch (error) {
      toast.error("Failed to load application links");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async (withEmail = false) => {
    setCreating(true);
    try {
      const data = withEmail ? newLinkData : {};
      const response = await axios.post(`${API}/admin/application-links`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreatedLink(response.data);
      toast.success("Application link created!");
      fetchLinks();
      setNewLinkData({ applicant_email: "", applicant_name: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create link");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const handleDeleteLink = async (linkId) => {
    if (!confirm("Are you sure you want to delete this link?")) return;
    
    try {
      await axios.delete(`${API}/admin/application-links/${linkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Link deleted");
      fetchLinks();
    } catch (error) {
      toast.error("Failed to delete link");
    }
  };

  const handleToggleActive = async (linkId, isCurrentlyActive) => {
    try {
      const endpoint = isCurrentlyActive ? "deactivate" : "activate";
      await axios.post(`${API}/admin/application-links/${linkId}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isCurrentlyActive ? "Link deactivated" : "Link activated");
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update link");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (link) => {
    if (link.status === "submitted" || link.is_used) {
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Submitted
        </Badge>
      );
    }
    if (link.status === "deactivated" || link.is_active === false) {
      return (
        <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
          <PowerOff className="w-3 h-3 mr-1" />
          Deactivated
        </Badge>
      );
    }
    if (link.status === "opened" || link.opened_at) {
      return (
        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
          <Clock className="w-3 h-3 mr-1" />
          Opened
        </Badge>
      );
    }
    return (
      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
        <Power className="w-3 h-3 mr-1" />
        Active
      </Badge>
    );
  };

  const stats = {
    total: links.length,
    active: links.filter(l => (l.status === "active" || l.status === "pending") && !l.opened_at && l.is_active !== false).length,
    opened: links.filter(l => (l.status === "opened" || l.opened_at) && !l.is_used && l.is_active !== false).length,
    submitted: links.filter(l => l.status === "submitted" || l.is_used).length,
    deactivated: links.filter(l => l.status === "deactivated" || l.is_active === false).length
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-application-links">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
                <Link2 className="w-8 h-8" />
                Application Links
              </h1>
              <p className="text-text-muted mt-1">Generate and manage application links for prospective tenants</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={fetchLinks}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-primary hover:bg-primary-800"
                data-testid="create-link-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Link
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="border-stone-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-primary">{stats.total}</p>
                <p className="text-xs text-text-muted">Total Links</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.active}</p>
                <p className="text-xs text-text-muted">Active</p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.opened}</p>
                <p className="text-xs text-text-muted">Opened</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.submitted}</p>
                <p className="text-xs text-text-muted">Submitted</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.deactivated}</p>
                <p className="text-xs text-text-muted">Deactivated</p>
              </CardContent>
            </Card>
          </div>

          {/* Links Table */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg">Generated Links ({links.length})</CardTitle>
              <CardDescription>
                Links remain active until used by the applicant or deactivated by admin
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : links.length === 0 ? (
                <div className="text-center py-8">
                  <Link2 className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-text-muted">No application links created yet</p>
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    className="mt-4 bg-primary hover:bg-primary-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Link
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Applicant</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Opened</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {links.map((link) => (
                        <TableRow key={link.id} data-testid={`link-row-${link.id}`}>
                          <TableCell>
                            <div>
                              {link.applicant_name ? (
                                <p className="font-medium flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {link.applicant_name}
                                </p>
                              ) : (
                                <p className="text-text-muted text-sm">Not specified</p>
                              )}
                              {link.applicant_email && (
                                <p className="text-xs text-text-muted flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {link.applicant_email}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(link)}</TableCell>
                          <TableCell className="text-sm">
                            {formatDate(link.created_at)}
                            <p className="text-xs text-text-muted">by {link.created_by_name}</p>
                          </TableCell>
                          <TableCell className="text-sm">
                            {link.opened_at ? formatDate(link.opened_at) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {!link.is_used && link.is_active !== false && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCopyLink(link.link_url)}
                                  title="Copy link"
                                  data-testid={`copy-link-${link.id}`}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              )}
                              {!link.is_used && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleActive(link.id, link.is_active !== false)}
                                  title={link.is_active !== false ? "Deactivate link" : "Activate link"}
                                  className={link.is_active !== false 
                                    ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50" 
                                    : "text-green-600 hover:text-green-700 hover:bg-green-50"}
                                  data-testid={`toggle-active-${link.id}`}
                                >
                                  {link.is_active !== false ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                                </Button>
                              )}
                              {link.application_id && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(`/admin/pending-approval`, '_self')}
                                  title="View application"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteLink(link.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete link"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Create Link Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Application Link</DialogTitle>
            <DialogDescription>
              Generate a one-time application link for a prospective tenant
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {!createdLink ? (
              <>
                <div className="p-4 bg-stone-50 rounded-xl space-y-4">
                  <h3 className="font-medium text-sm">Option 1: Quick Generate</h3>
                  <p className="text-xs text-text-muted">Create a link without specifying applicant details</p>
                  <Button
                    onClick={() => handleCreateLink(false)}
                    disabled={creating}
                    className="w-full bg-primary hover:bg-primary-800"
                    data-testid="quick-generate-btn"
                  >
                    {creating ? "Creating..." : "Generate Link"}
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-text-muted">Or</span>
                  </div>
                </div>

                <div className="p-4 bg-stone-50 rounded-xl space-y-4">
                  <h3 className="font-medium text-sm">Option 2: With Applicant Details</h3>
                  <p className="text-xs text-text-muted">Track who the link was sent to</p>
                  
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Applicant Name</Label>
                      <Input
                        placeholder="Enter applicant's name"
                        value={newLinkData.applicant_name}
                        onChange={(e) => setNewLinkData({...newLinkData, applicant_name: e.target.value})}
                        data-testid="applicant-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Applicant Email</Label>
                      <Input
                        type="email"
                        placeholder="Enter applicant's email"
                        value={newLinkData.applicant_email}
                        onChange={(e) => setNewLinkData({...newLinkData, applicant_email: e.target.value})}
                        data-testid="applicant-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={() => handleCreateLink(true)}
                    disabled={creating || (!newLinkData.applicant_name && !newLinkData.applicant_email)}
                    className="w-full bg-primary hover:bg-primary-800"
                    data-testid="generate-with-details-btn"
                  >
                    {creating ? "Creating..." : "Generate Link"}
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-700">Link Created!</span>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg border border-green-200 mb-3">
                    <p className="text-xs text-text-muted mb-1">Application Link:</p>
                    <p className="text-sm font-mono break-all">{createdLink.link_url}</p>
                  </div>

                  <Button
                    onClick={() => handleCopyLink(createdLink.link_url)}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="copy-created-link-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> This link will remain active until the applicant submits their application or you deactivate it.
                    Share it via WhatsApp, SMS, or email.
                  </p>
                </div>

                <Button
                  variant="outline"
                  onClick={() => {
                    setCreatedLink(null);
                    setShowCreateDialog(false);
                  }}
                  className="w-full"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
